/**
 * The postal code table is ~320 KB and only ever needed once an address form is open, so it is
 * loaded on demand rather than pulled into the bundle alongside the rest of the address data.
 */
let postalCodesPromise: Promise<Record<string, string | Record<string, string>>> | null = null

function loadPostalCodes() {
  postalCodesPromise ??= import('@script/address/postal-codes').then((module) => module.postalCodesByDistrict)
  return postalCodesPromise
}

/**
 * Looks up the postal code for a village id.
 *
 * @param villageId - A 10-digit village id from `getVillagesByDistrict` (first 7 digits are the district).
 * @returns The postal code, or `null` when the village is not covered by the dataset.
 *
 * @remarks
 * Roughly 2.4% of villages have no entry — mostly regencies renamed after the address dataset's
 * snapshot. Callers should treat `null` as "leave the field for the user to fill in", never as an
 * error, and should never overwrite a value the user typed themselves.
 *
 * @example
 * await getPostalCodeByVillageId('3171090002') // '12810' (Tebet Barat, Jakarta Selatan)
 */
export async function getPostalCodeByVillageId(villageId: string): Promise<string | null> {
  if (villageId.length < 8) return null

  const postalCodes = await loadPostalCodes()
  const district = postalCodes[villageId.slice(0, 7)]

  if (district == null) return null
  if (typeof district === 'string') return district

  return district[villageId.slice(7)] ?? null
}
